/**
 * 📈 GA4 ANALYTICS UTILITY — single source of truth for Google Analytics 4.
 *
 * Measurement ID: G-8LBGJYDV1S
 *
 * RULES ENFORCED HERE (do not bypass this module with raw gtag calls):
 *  - gtag.js is loaded exactly ONCE per document.
 *  - `send_page_view` is disabled on config so SPA route changes are the ONLY
 *    source of page_view events (no duplicates on first load).
 *  - Every call is a no-op when window.gtag is unavailable — analytics can
 *    never crash or block the app.
 *  - `trackGA4EventOnce` guards against React Strict Mode double-effects,
 *    re-renders and repeated state updates.
 *  - PRIVACY: only predefined categories / booleans / numeric scores are sent.
 *    Never conversation text, screenshots, report bodies, names, emails,
 *    phone numbers, tokens, or user ids.
 */
export const GA4_MEASUREMENT_ID = 'G-8LBGJYDV1S'
const GTAG_SRC = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`
type GA4Params = Record<string, string | number | boolean | undefined>
declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}
const isDev = (): boolean => {
  try {
    return Boolean((import.meta as any)?.env?.DEV)
  } catch {
    return false
  }
}
/** Development-only console trace. Never shown to production users. */
const debugLog = (eventName: string, params?: GA4Params) => {
  if (!isDev()) return
  if (params && Object.keys(params).length > 0) {
    console.log(`[GA4] ${eventName}`, params)
  } else {
    console.log(`[GA4] ${eventName}`)
  }
}
let initialized = false
/**
 * Loads gtag.js once and configures the property.
 * Safe to call from multiple entry points / components — repeat calls no-op.
 */
export function initGA4(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (initialized) return
  initialized = true
  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments)
    }
  }
  const alreadyLoaded = document.querySelector(
    'script[src*="googletagmanager.com/gtag/js"]',
  )
  if (!alreadyLoaded) {
    const script = document.createElement('script')
    script.async = true
    script.src = GTAG_SRC
    document.head.appendChild(script)
    window.gtag('js', new Date())
    window.gtag('config', GA4_MEASUREMENT_ID, {
      // Route changes fire page_view explicitly — prevents duplicates.
      send_page_view: false,
    })
  }
  debugLog('initialized', {
    measurement_id: GA4_MEASUREMENT_ID,
  })
}
/** Core event sender. Fails silently when gtag is unavailable. */
export function trackGA4Event(eventName: string, params: GA4Params = {}): void {
  try {
    if (typeof window === 'undefined') return
    const payload: GA4Params = {}
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) payload[key] = value
    })
    debugLog(eventName, payload)
    if (typeof window.gtag !== 'function') return
    window.gtag('event', eventName, payload)
  } catch {
    // Analytics must never break the application.
  }
}
const firedKeys = new Set<string>()
/**
 * Fires an event at most once per page session for a given dedupe key.
 * Use for anything mount-driven (Strict Mode double-invokes effects in dev).
 */
export function trackGA4EventOnce(
  dedupeKey: string,
  eventName: string,
  params: GA4Params = {},
): void {
  if (firedKeys.has(dedupeKey)) return
  firedKeys.add(dedupeKey)
  trackGA4Event(eventName, params)
}
/**
 * Fires an event at most once per browser (persisted).
 * Use for irreversible funnel milestones that must survive a page refresh,
 * e.g. onboarding_completed or purchase.
 */
export function trackGA4EventOncePersisted(
  storageKey: string,
  eventName: string,
  params: GA4Params = {},
): void {
  try {
    if (localStorage.getItem(storageKey) === 'true') return
    localStorage.setItem(storageKey, 'true')
  } catch {
    // localStorage unavailable (private mode) — fall back to session dedupe.
    if (firedKeys.has(storageKey)) return
    firedKeys.add(storageKey)
  }
  trackGA4Event(eventName, params)
}
const currentPage = (): string => {
  if (typeof window === 'undefined') return ''
  return window.location.pathname
}
let lastTrackedLocation: string | null = null
/**
 * Tracks a page view. Deduped on the full path so a re-render or Strict Mode
 * double-effect cannot produce two page_view hits for one navigation.
 */
export function trackPageView(path?: string, title?: string): void {
  if (typeof window === 'undefined') return
  const resolvedPath =
    path || `${window.location.pathname}${window.location.search}`
  if (lastTrackedLocation === resolvedPath) return
  lastTrackedLocation = resolvedPath
  trackGA4Event('page_view', {
    page_location: `${window.location.origin}${resolvedPath}`,
    page_path: resolvedPath,
    page_title: title || document.title,
  })
}
// ====================================================================
// 🎯 ONBOARDING (7 questions: 1 = profile, 2-7 = screening questions)
// ====================================================================
export function trackOnboardingStarted(): void {
  trackGA4EventOnce('onboarding_started', 'onboarding_started', {
    question_number: 1,
    page: currentPage(),
    timestamp: new Date().toISOString(),
  })
}
/**
 * @param questionNumber 1-7
 * @param answerOption predefined categorical id ONLY (e.g. "option_yes").
 *        Never a free-form or personally identifying answer.
 */
export function trackOnboardingQuestionAnswered(
  questionNumber: number,
  answerOption: string,
): void {
  if (questionNumber < 1 || questionNumber > 7) return
  trackGA4Event(`onboarding_question_${questionNumber}`, {
    answer: answerOption,
    answer_option: answerOption,
    question_number: questionNumber,
  })
}
export function trackOnboardingCompleted(): void {
  trackGA4EventOncePersisted(
    'ga4_onboarding_completed_fired',
    'onboarding_completed',
    {
      completed_questions: 7,
      page: currentPage(),
    },
  )
}
export function trackOnboardingSkipped(
  questionNumber: number,
  completedQuestions: number,
): void {
  trackGA4Event('onboarding_skipped', {
    question_number: questionNumber,
    completed_questions: completedQuestions,
  })
}
// ====================================================================
// 🔍 SCANS
// ====================================================================
export type ScanInputType = 'text' | 'screenshot' | 'text_and_screenshot'
export function trackScanStarted(
  scanMode: string,
  inputType: ScanInputType,
): void {
  trackGA4Event('toxicdetector_scan_started', {
    scan_mode: scanMode,
    input_type: inputType,
    scan_type: 'forensic',
    page: currentPage(),
  })
}
export function trackScanCompleted(
  scanMode: string,
  inputType: ScanInputType,
  riskLevel?: string,
  toxicityScore?: number,
): void {
  trackGA4Event('toxicdetector_scan_completed', {
    scan_mode: scanMode,
    input_type: inputType,
    risk_level: riskLevel || 'unknown',
    result_category: riskLevel || 'unknown',
    toxicity_score:
      typeof toxicityScore === 'number' ? Math.round(toxicityScore) : undefined,
    completed: true,
  })
}
export function trackScanFailed(
  scanMode: string,
  inputType: ScanInputType,
  reason: string,
): void {
  trackGA4Event('scan_failed', {
    scan_mode: scanMode,
    input_type: inputType,
    failure_reason: reason,
  })
}
export function trackAnalysisViewed(riskLevel?: string): void {
  trackGA4Event('analysis_viewed', {
    result_category: riskLevel || 'unknown',
    page: currentPage(),
  })
}
// ====================================================================
// 📤 SHARING & EXPORT
// ====================================================================
export type ShareMethod =
  | 'copy_link'
  | 'native_share'
  | 'whatsapp'
  | 'twitter'
  | 'facebook'
  | 'instagram'
  | 'tiktok'
  | 'youtube'
  | 'other'
export function trackResultShared(
  shareMethod: ShareMethod,
  contentType: string,
): void {
  // Note: the share URL itself is intentionally NOT sent — it carries a scan id.
  trackGA4Event('toxicdetector_share', {
    share_method: shareMethod,
    content_type: contentType,
    page: currentPage(),
  })
  trackGA4Event('result_shared', {
    share_method: shareMethod,
    content_type: contentType,
  })
}
export function trackResultDownloaded(format: 'png' | 'pdf'): void {
  trackGA4Event('result_downloaded', {
    file_format: format,
    page: currentPage(),
  })
}
// ====================================================================
// 🔐 AUTHENTICATION (no email, no password, no tokens, no user ids)
// ====================================================================
export function trackSignup(method: 'email' | 'google'): void {
  trackGA4Event('signup', {
    method,
  })
}
export function trackLogin(method: 'email' | 'google'): void {
  trackGA4Event('login', {
    method,
  })
}
export function trackLogout(): void {
  trackGA4Event('logout', {})
}
// ====================================================================
// 💳 REVENUE FUNNEL
// ====================================================================
export function trackPricingViewed(source: string): void {
  trackGA4EventOnce(`pricing_viewed_${source}`, 'pricing_viewed', {
    source,
    page: currentPage(),
  })
}
export function trackCheckoutStarted(plan: string, source: string): void {
  trackGA4Event('checkout_started', {
    plan,
    source,
  })
}
/** Call ONLY after a payment has actually been confirmed as successful. */
export function trackPurchase(reference: string, plan: string): void {
  trackGA4EventOncePersisted(`ga4_purchase_${reference}`, 'purchase', {
    plan,
  })
  const normalized = plan.toLowerCase()
  const isSubscription =
    normalized.includes('pro') ||
    normalized.includes('unlimited') ||
    normalized.includes('month')
  if (isSubscription) {
    trackGA4EventOncePersisted(
      `ga4_subscription_${reference}`,
      'subscription_started',
      {
        plan,
      },
    )
  }
}
// ====================================================================
// ✍️ REWRITER
// ====================================================================
export function trackRewriteCompleted(tone: string): void {
  trackGA4Event('rewrite_completed', {
    tone,
    page: currentPage(),
  })
}
